// Redux
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";

// Constant
import { LoadingStates, RoleManagement as Constants, UNAUTHORISED } from "constants/global";

// API
import {
	getUsers as APIGetUser,
	getGroupPermissionList as APIGetGroupPermissionList,
	createGroup as APICreateGroup,
	toggleGroupActivation as APIToggleGroupActivation,
	getGroupList as APIGetGroupList,
	editGroup as APIEditGroup,
	createUser as APICreateUser,
	getUserList as APIGetUserList,
	getCardEnquiryDetails as APIGetCardEnquiryDetails,
	getMemberEnquiryDetails as APIGetMemberEnquiryDetails,
	deactivateUser as APIDeactivateUser,
	activateUser as APIActivateUser,
	resendTemporaryPassword as APIResendTemporaryPassword,
	editUser as APIEditUser,
	getConfigData as APIGetConfigData,
	getStoreList as APIGetStoreList,
	delinkMember as APIDelinkMember
} from "services/api/user";

// Types
import type { RootState, Users, Filter, UserPermissionType } from "redux/types";
import {
	type ApiUserResponse,
	type ApiErrorResponse,
	type GroupPermissionListResponse,
	type CreateGroupResponse,
	type ToggleGroupActivationResponse,
	type GroupListResponse,
	type EditGroupResponse,
	type CreateUserResponse,
	type DeactivateUserResponse,
	type ActivateUserResponse,
	type ResendTemporaryPasswordResponse,
	type EditUserResponse,
	type GetConfigDataResponse,
	type GetStoreListResponse,
	type CardEnquiryResponse,
	type MemberEnquiryResponse,
	type DelinkMemberResponse,
	type GetLastUpdatedOutletListResponse,
	type CreateOutletPayload,
	type CreateOutletResponse,
	type GetOutletPayload,
	type OutletItemV2,
	type GetTagsResponse,
	type TagItem,
	type CategoryType3TagItem,
	GetCategoryType3TagsResponse
} from "services/api/types";

// Utils
import { getUserPermissionList } from "utils/Generic/generic";
import {
	getLastUpdatedOutletList as APIGetLastUpdatedOutletList,
	createOutlet as APICreateOutlet,
	getOutlet as APIGetOutlet,
	getTags as APIGetTags,
	getCategoryType3Tags as APIGetCategoryType3Tags
} from "services/api/outletManagement";

// Defaults
export const initialState: Users = {
	loading: LoadingStates.DEFAULT,
	user: undefined,
	userToken: undefined,
	cardEnquiryDetails: {
		details: {},
		loading: LoadingStates.DEFAULT
	},
	memberEnquiryDetails: {
		details: {},
		loading: LoadingStates.DEFAULT
	},
	delinkMember: {
		details: {},
		loading: LoadingStates.DEFAULT
	},
	waitforAuthcompletion: false,
	userGroupList: {
		loading: LoadingStates.DEFAULT
	},
	userList: {
		loading: LoadingStates.DEFAULT,
		list: undefined
	},
	userSearchKeyPressed: false,
	showFilter: {},
	userPermissionList: [],
	currentGroup: Constants.ALL_USERS_GROUP.group_code,
	selectedFilters: {},
	selectedRowsPerPage: {},
	selectedOutletCode: "",
	selectedPageSize: {},
	hasUnaurizedError: false,
	groups: {
		permissionsList: [],
		groupsList: {},
		loading: LoadingStates.DEFAULT,
		selectedGroupDetails: {},
		createGroup: {},
		editGroup: {},
		toggleGroupActivation: {}
	},
	adminUser: {
		selectedUserDetails: {},
		loading: LoadingStates.DEFAULT,
		createUser: {},
		editUser: {},
		activateUser: {},
		deactivateUser: {},
		resendTemporaryPassword: {}
	},
	configData: {
		configuration: {},
		adminEmail: "",
		inactiveTime: 0,
		loading: LoadingStates.DEFAULT
	},
	storeList: {
		stores: {},
		loading: LoadingStates.DEFAULT
	},
	lastUpdatedOutletList: {
		loading: LoadingStates.DEFAULT,
		outlets: []
	},
	createOutlet: {
		loading: LoadingStates.DEFAULT
	},
	getOutlet: {
		loading: LoadingStates.DEFAULT,
		outlet: undefined
	},
	getTags: {
		loading: LoadingStates.DEFAULT,
		tags: []
	},
	getCategoryType3Tags: {
		loading: LoadingStates.DEFAULT,
		tags: []
	},
	filterInputs: {
		receipt: "",
		outlet: "",
		outletName: "",
		outletCode: ""
	}
};

// Thunks

export const getUserAuthDetails = createAsyncThunk<ApiUserResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>("GET_USERS", async (_, { rejectWithValue }) => {
	try {
		// Call API
		const { data } = await APIGetUser();

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getUserList = createAsyncThunk<
	ApiUserResponse,
	{
		page_number?: number;
		page_records?: number;
		user_search_text?: string;
		group_code?: string;
		tenant_code?: string;
	},
	{ rejectValue: ApiErrorResponse; state: RootState }
>("GET_USER_LIST", async (payload, { rejectWithValue }) => {
	try {
		// Call API
		const { data } = await APIGetUserList(payload);
		return { ...data, selectedGroupCode: payload.group_code ? payload.group_code : Constants.ALL_USERS_GROUP.group_code };
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getUserDetails = createAsyncThunk<ApiUserResponse, { username: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_USER_DETAILS",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetUserList(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getCardEnquiryDetails = createAsyncThunk<CardEnquiryResponse, { cr_card_number: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_CARD_ENQUIRY_DETAILS",
	async (payload, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIGetCardEnquiryDetails(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const delinkMember = createAsyncThunk<DelinkMemberResponse, { account: string; MemberID?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"DELINK_MEMBER",
	async (payload, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIDelinkMember(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getMemberEnquiryDetails = createAsyncThunk<MemberEnquiryResponse, { member_id: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_MEMBER_ENQUIRY_DETAILS",
	async (payload, { rejectWithValue }) => {
		try {
			// Call API
			const { data } = await APIGetMemberEnquiryDetails(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

const getGroupListServiceCall = async (payload: { group_code?: string; status?: string }, rejectWithValue: (value: ApiErrorResponse) => any) => {
	try {
		const { data } = await APIGetGroupList(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
};

export const getGroupsList = createAsyncThunk<GroupListResponse, { group_code?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GROUPS_LIST",
	async (payload, { rejectWithValue }) => {
		return getGroupListServiceCall(payload, rejectWithValue);
	}
);

export const getActiveGroupsList = createAsyncThunk<GroupListResponse, { status?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"ACTIVE_GROUP_LIST",
	async (payload, { rejectWithValue }) => {
		return getGroupListServiceCall(payload, rejectWithValue);
	}
);

export const getGroupDetails = createAsyncThunk<GroupListResponse, { group_code?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GROUP_DETAILS",
	async (payload, { rejectWithValue }) => {
		return getGroupListServiceCall(payload, rejectWithValue);
	}
);

export const getGroupPermissionList = createAsyncThunk<GroupPermissionListResponse, { perm_group_code?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_GROUP_PERMISSION_LIST",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetGroupPermissionList(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const createGroup = createAsyncThunk<
	CreateGroupResponse,
	{ group_code: string; group_desc: string; permissions: Array<string> },
	{ rejectValue: ApiErrorResponse; state: RootState }
>("CREATE_NEW_GROUP", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APICreateGroup(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const toggleGroupActivation = createAsyncThunk<
	ToggleGroupActivationResponse,
	{ group_id: string; is_activate: boolean },
	{ rejectValue: ApiErrorResponse; state: RootState }
>("TOGGLE_GROUP_ACTIVATION", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIToggleGroupActivation(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const editGroup = createAsyncThunk<
	EditGroupResponse,
	{ group_id: string; group_code: string; group_desc: string; permissions: Array<string> },
	{ rejectValue: ApiErrorResponse; state: RootState }
>("EDIT_GROUP", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIEditGroup(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const createUser = createAsyncThunk<
	CreateUserResponse,
	{ username: string; email: string; groups: Array<string>; first_name?: string; last_name?: string; mfa_enabled?: string; tenant_code?: string },
	{ rejectValue: ApiErrorResponse; state: RootState }
>("CREATE_USER", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APICreateUser(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const editUser = createAsyncThunk<
	EditUserResponse,
	{ username: string; email: string; groups: Array<string>; first_name?: string; last_name?: string; mfa_enabled?: string; tenant_code?: string },
	{ rejectValue: ApiErrorResponse; state: RootState }
>("EDIT_USER", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIEditUser(payload);

		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const deactivateUser = createAsyncThunk<DeactivateUserResponse, { username: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"DEACTIVATE_USER",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIDeactivateUser(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const activateUser = createAsyncThunk<ActivateUserResponse, { username: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"ACTIVATE_USER",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIActivateUser(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const resendTemporaryPassword = createAsyncThunk<ResendTemporaryPasswordResponse, { username: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"RESEND_TEMPORARY_PASSWORD",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIResendTemporaryPassword(payload);

			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getConfigData = createAsyncThunk<GetConfigDataResponse, { config_mst_code?: string; config_dtl_code?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_CONFIG_DATA",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetConfigData(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getAdminEmail = createAsyncThunk<GetConfigDataResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_CONFIG_DATA_ADMIN_EMAIL",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await APIGetConfigData({
				config_dtl_code: "1001"
			});
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getInactiveTime = createAsyncThunk<GetConfigDataResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_CONFIG_DATA_INACTIVE_TIME",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await APIGetConfigData({
				config_dtl_code: "1003"
			});
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getStoreList = createAsyncThunk<GetStoreListResponse, { tenant_code?: string }, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_STORE_LIST",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetStoreList(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getLastUpdatedOutletList = createAsyncThunk<GetLastUpdatedOutletListResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_LAST_UPDATED_OUTLET_LIST",
	async (_, { rejectWithValue }) => {
		try {
			const { data } = await APIGetLastUpdatedOutletList();
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const createOutlet = createAsyncThunk<CreateOutletResponse, CreateOutletPayload, { rejectValue: ApiErrorResponse; state: RootState }>(
	"CREATE_OUTLET",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APICreateOutlet(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getOutlet = createAsyncThunk<GetLastUpdatedOutletListResponse, GetOutletPayload, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_OUTLET",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetOutlet(payload);
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

export const getTags = createAsyncThunk<GetTagsResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>("GET_TAGS", async (payload, { rejectWithValue }) => {
	try {
		const { data } = await APIGetTags();
		return data;
	} catch (error) {
		return rejectWithValue(error as ApiErrorResponse);
	}
});

export const getCategoryType3Tags = createAsyncThunk<GetCategoryType3TagsResponse, undefined, { rejectValue: ApiErrorResponse; state: RootState }>(
	"GET_CATEGORY_TYPE_3_TAGS",
	async (payload, { rejectWithValue }) => {
		try {
			const { data } = await APIGetCategoryType3Tags();
			return data;
		} catch (error) {
			return rejectWithValue(error as ApiErrorResponse);
		}
	}
);

// Helper
const setLoading = (draft: Users): void => {
	draft.loading = LoadingStates.LOADING;
};

export const removeLoading = (draft: Users): void => {
	draft.loading = LoadingStates.IDLE;
};

const setAuthError = (draft: Users, error: any): void => {
	draft.hasUnaurizedError = error?.response?.status === 401;
};

const setAuthTokenError = (draft: Users, error: any): void => {
	if (error?.return_description === UNAUTHORISED) {
		draft.hasUnaurizedError = true;
	}
};

// Slice
export const userSlice = createSlice({
	name: "USER",
	initialState,
	reducers: {
		resetAuthErr: (state) => {
			return { ...state, hasUnaurizedError: false };
		},
		updateAuthFlowStatus: (state, { payload }) => {
			return { ...state, waitforAuthcompletion: payload };
		},
		resetAppData: () => {
			return { ...initialState };
		},
		updateUserToken: (state, { payload }) => {
			return { ...state, userToken: payload };
		},
		resetUserDetails: (state) => {
			return { ...state, userToken: null, user: null, hasUnaurizedError: false };
		},
		resetCreateGroup: (state) => {
			return { ...state, groups: { ...state.groups, createGroup: {} } };
		},
		updateUserSearchKeyPressed: (state, { payload }) => {
			return { ...state, userSearchKeyPressed: payload, currentGroup: Constants.ALL_USERS_GROUP.group_code };
		},
		toggleFilter: (state, { payload }) => {
			return { ...state, showFilter: { ...state.showFilter, ...payload } };
		},
		clearFilter: (state, { payload }) => {
			return { ...state, showFilter: { ...payload } };
		},
		updatedSelectedFilter: (state, action: PayloadAction<{ [key: string]: string | string[] }>) => {
			// If the value is an array (for Filter by checkboxes)
			if (Array.isArray(action.payload["Filter by"])) {
				state.selectedFilters = action.payload["Filter by"].reduce((acc, value, index) => {
					acc[index] = value;
					return acc;
				}, {} as { [key: string]: string });
			} else {
				// For other filter types (like sort)
				state.selectedFilters = {
					...state.selectedFilters,
					...action.payload
				};
			}
		},
		updateCurrentGroup: (state, { payload }) => {
			return { ...state, currentGroup: payload };
		},
		updatedselectedRowsPerPage: (state, { payload }) => {
			return { ...state, selectedRowsPerPage: { ...state.selectedRowsPerPage, ...payload } };
		},
		updateSelectedOutletCode: (state, { payload }) => {
			return { ...state, selectedOutletCode: payload };
		},
		updatedSelectedPageSize: (state, { payload }) => {
			return { ...state, selectedPageSize: { ...state.selectedPageSize, ...payload } };
		},
		resetToggleGroupActivation: (state) => {
			return { ...state, groups: { ...state.groups, toggleGroupActivation: {} } };
		},
		resetEditGroup: (state) => {
			return { ...state, groups: { ...state.groups, editGroup: {} } };
		},
		resetSelectedGroupDetails: (state) => {
			return { ...state, groups: { ...state.groups, selectedGroupDetails: {} } };
		},
		resetCreateUser: (state) => {
			return { ...state, adminUser: { ...state.adminUser, createUser: {} } };
		},
		resetEditUser: (state) => {
			return { ...state, adminUser: { ...state.adminUser, editUser: {} } };
		},
		resetDeactivateUser: (state) => {
			return { ...state, adminUser: { ...state.adminUser, deactivateUser: {} } };
		},
		resetActivateUser: (state) => {
			return { ...state, adminUser: { ...state.adminUser, activateUser: {} } };
		},
		resetResendTemporaryPassword: (state) => {
			return { ...state, adminUser: { ...state.adminUser, resendTemporaryPassword: {} } };
		},
		resetSelectedAdminDetails: (state) => {
			return { ...state, adminUser: { ...state.adminUser, selectedUserDetails: {} } };
		},
		resetEnquiryDetails: (state) => {
			return { ...state, cardEnquiryDetails: { details: {}, loading: LoadingStates.DEFAULT }, memberEnquiryDetails: { details: {}, loading: LoadingStates.DEFAULT } };
		},
		updateFilterInputs: (state, { payload }) => {
			return {
				...state,
				filterInputs: {
					...state.filterInputs,
					[payload.filterType]: payload.value
				}
			};
		}
	},
	extraReducers: (builder) => {
		builder.addCase(getUserAuthDetails.fulfilled, (draft: Users, { payload }) => {
			const { data } = payload;
			draft.user = payload;
			draft.userPermissionList = getUserPermissionList(payload);
			draft.selectedOutletCode = data[0]?.outlets?.[0]?.outlet_code;
			draft.loading = LoadingStates.IDLE;
		});
		builder.addCase(getUserAuthDetails.pending, setLoading);
		builder.addCase(getUserAuthDetails.rejected, (draft: Users, { payload }) => {
			const error = payload as any;

			draft.user = { errors: error?.response?.data?.errors || error?.name };
			removeLoading(draft);
			setAuthTokenError(draft, payload);
		});
		// Card enquiry
		builder.addCase(getCardEnquiryDetails.fulfilled, (draft: Users, { payload }) => {
			draft.cardEnquiryDetails.details = payload;
			draft.memberEnquiryDetails.details = undefined;
			draft.cardEnquiryDetails.loading = LoadingStates.IDLE;
		});
		builder.addCase(getCardEnquiryDetails.pending, (draft: Users) => {
			draft.cardEnquiryDetails.loading = LoadingStates.LOADING;
		});
		builder.addCase(getCardEnquiryDetails.rejected, (draft: Users, { payload }) => {
			const error = payload as any;
			draft.cardEnquiryDetails.details = { errors: error?.response?.data?.errors || error?.name };
			draft.cardEnquiryDetails.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// member enquiry
		builder.addCase(getMemberEnquiryDetails.fulfilled, (draft: Users, { payload }) => {
			draft.memberEnquiryDetails.details = payload;
			draft.cardEnquiryDetails.details = undefined;
			draft.memberEnquiryDetails.loading = LoadingStates.IDLE;
		});
		builder.addCase(getMemberEnquiryDetails.pending, (draft: Users) => {
			draft.memberEnquiryDetails.loading = LoadingStates.LOADING;
		});
		builder.addCase(getMemberEnquiryDetails.rejected, (draft: Users, { payload }) => {
			const error = payload as any;
			draft.memberEnquiryDetails.details = { errors: error?.response?.data?.errors || error?.name };
			draft.memberEnquiryDetails.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// delink member
		builder.addCase(delinkMember.fulfilled, (draft: Users, { payload }) => {
			draft.delinkMember.details = payload;
			draft.delinkMember.loading = LoadingStates.IDLE;
		});
		builder.addCase(delinkMember.pending, (draft: Users) => {
			draft.delinkMember.loading = LoadingStates.LOADING;
		});
		builder.addCase(delinkMember.rejected, (draft: Users, { payload }) => {
			draft.delinkMember.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Group permission list state
		builder.addCase(getGroupPermissionList.fulfilled, (draft: Users, { payload }) => {
			draft.groups.permissionsList = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(getGroupPermissionList.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(getGroupPermissionList.rejected, (draft: Users, { payload }) => {
			setAuthError(draft, payload);
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// User List
		builder.addCase(getUserList.fulfilled, (draft: Users, { payload }) => {
			draft.userGroupList[payload.selectedGroupCode] = payload.data;
			draft.userSearchKeyPressed = false;
			draft.userGroupList.loading = LoadingStates.IDLE;
		});

		builder.addCase(getUserList.pending, (draft: Users) => {
			draft.userGroupList.loading = LoadingStates.LOADING;
		});
		builder.addCase(getUserList.rejected, (draft: Users, { payload }) => {
			draft.userGroupList.loading = LoadingStates.IDLE;
			draft.userSearchKeyPressed = false;
			setAuthTokenError(draft, payload);
		});

		// Get updated outlet list
		builder.addCase(getLastUpdatedOutletList.fulfilled, (draft: Users, { payload }) => {
			draft.lastUpdatedOutletList.loading = LoadingStates.IDLE;
			draft.lastUpdatedOutletList.outlets = payload.data;
		});
		builder.addCase(getLastUpdatedOutletList.pending, (draft: Users) => {
			draft.lastUpdatedOutletList.loading = LoadingStates.LOADING;
		});

		// Create/ update outlet
		builder.addCase(createOutlet.pending, (draft: Users) => {
			draft.createOutlet.loading = LoadingStates.LOADING;
		});
		builder.addCase(createOutlet.fulfilled, (draft: Users) => {
			draft.createOutlet.loading = LoadingStates.IDLE;
		});
		builder.addCase(createOutlet.rejected, (draft: Users) => {
			draft.createOutlet.loading = LoadingStates.IDLE;
		});

		// Get outlet
		builder.addCase(getOutlet.pending, (draft: Users) => {
			draft.getOutlet.outlet = undefined;
			draft.getOutlet.loading = LoadingStates.LOADING;
		});
		builder.addCase(getOutlet.fulfilled, (draft: Users, { payload }) => {
			draft.getOutlet.loading = LoadingStates.IDLE;
			draft.getOutlet.outlet = payload.data[0];
		});
		builder.addCase(getOutlet.rejected, (draft: Users) => {
			draft.getOutlet.outlet = undefined;
			draft.getOutlet.loading = LoadingStates.IDLE;
		});

		// Get tags
		builder.addCase(getTags.pending, (draft: Users) => {
			draft.getTags.loading = LoadingStates.LOADING;
			draft.getTags.tags = [];
		});
		builder.addCase(getTags.fulfilled, (draft: Users, { payload }) => {
			draft.getTags.loading = LoadingStates.IDLE;
			draft.getTags.tags = payload.data;
		});
		builder.addCase(getTags.rejected, (draft: Users) => {
			draft.getTags.loading = LoadingStates.IDLE;
			draft.getTags.tags = [];
		});

		// Get category type 3 tags
		builder.addCase(getCategoryType3Tags.pending, (draft: Users) => {
			draft.getCategoryType3Tags.loading = LoadingStates.LOADING;
			draft.getCategoryType3Tags.tags = [];
		});
		builder.addCase(getCategoryType3Tags.fulfilled, (draft: Users, { payload }) => {
			draft.getCategoryType3Tags.loading = LoadingStates.IDLE;
			draft.getCategoryType3Tags.tags = payload.data;
		});
		builder.addCase(getCategoryType3Tags.rejected, (draft: Users) => {
			draft.getCategoryType3Tags.loading = LoadingStates.IDLE;
			draft.getCategoryType3Tags.tags = [];
		});

		// Selected users details
		builder.addCase(getUserDetails.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.selectedUserDetails = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});

		builder.addCase(getUserDetails.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(getUserDetails.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Create group state
		builder.addCase(createGroup.fulfilled, (draft: Users, { payload }) => {
			draft.groups.createGroup = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(createGroup.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(createGroup.rejected, (draft: Users, { payload }) => {
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Toggle group activation state
		builder.addCase(toggleGroupActivation.fulfilled, (draft: Users, { payload }) => {
			draft.groups.toggleGroupActivation = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(toggleGroupActivation.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(toggleGroupActivation.rejected, (draft: Users, { payload }) => {
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get group lists state
		builder.addCase(getGroupsList.fulfilled, (draft: Users, { payload }) => {
			draft.groups.groupsList = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(getGroupsList.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(getGroupsList.rejected, (draft: Users, { payload }) => {
			setAuthError(draft, payload);
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get Active Group List
		builder.addCase(getActiveGroupsList.fulfilled, (draft: Users, { payload }) => {
			draft.groups.groupsList = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(getActiveGroupsList.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(getActiveGroupsList.rejected, (draft: Users, { payload }) => {
			setAuthError(draft, payload);
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get group details state
		builder.addCase(getGroupDetails.fulfilled, (draft: Users, { payload }) => {
			draft.groups.selectedGroupDetails = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(getGroupDetails.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(getGroupDetails.rejected, (draft: Users, { payload }) => {
			setAuthError(draft, payload);
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Edit group details state
		builder.addCase(editGroup.fulfilled, (draft: Users, { payload }) => {
			draft.groups.editGroup = payload;
			draft.groups.loading = LoadingStates.IDLE;
		});
		builder.addCase(editGroup.pending, (draft: Users) => {
			draft.groups.loading = LoadingStates.LOADING;
		});
		builder.addCase(editGroup.rejected, (draft: Users, { payload }) => {
			draft.groups.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Create user state
		builder.addCase(createUser.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.createUser = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});
		builder.addCase(createUser.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(createUser.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Deactivate user state
		builder.addCase(deactivateUser.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.deactivateUser = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});
		builder.addCase(deactivateUser.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(deactivateUser.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Activate user state
		builder.addCase(activateUser.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.activateUser = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});
		builder.addCase(activateUser.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(activateUser.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Resend temporary password state
		builder.addCase(resendTemporaryPassword.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.resendTemporaryPassword = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});
		builder.addCase(resendTemporaryPassword.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(resendTemporaryPassword.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Edit user state
		builder.addCase(editUser.fulfilled, (draft: Users, { payload }) => {
			draft.adminUser.editUser = payload;
			draft.adminUser.loading = LoadingStates.IDLE;
		});
		builder.addCase(editUser.pending, (draft: Users) => {
			draft.adminUser.loading = LoadingStates.LOADING;
		});
		builder.addCase(editUser.rejected, (draft: Users, { payload }) => {
			draft.adminUser.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get Config Data
		builder.addCase(getConfigData.fulfilled, (draft: Users, { payload }) => {
			draft.configData.configuration = payload;
			draft.configData.loading = LoadingStates.IDLE;
		});
		builder.addCase(getConfigData.pending, (draft: Users) => {
			draft.configData.loading = LoadingStates.LOADING;
		});
		builder.addCase(getConfigData.rejected, (draft: Users, { payload }) => {
			draft.configData.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get Admin Email Data
		builder.addCase(getAdminEmail.fulfilled, (draft: Users, { payload }) => {
			const { data } = payload;
			draft.configData.adminEmail = data?.[0].config_dtl_val1 ?? "";
			draft.configData.loading = LoadingStates.IDLE;
		});
		builder.addCase(getAdminEmail.pending, (draft: Users) => {
			draft.configData.loading = LoadingStates.LOADING;
		});
		builder.addCase(getAdminEmail.rejected, (draft: Users, { payload }) => {
			draft.configData.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});

		// Get Config Inactive Time
		builder.addCase(getInactiveTime.fulfilled, (draft: Users, { payload }) => {
			const { data } = payload;
			draft.configData.inactiveTime = data?.[0].config_dtl_val3 ?? 30;
			draft.configData.loading = LoadingStates.IDLE;
		});
		builder.addCase(getInactiveTime.pending, (draft: Users) => {
			draft.configData.loading = LoadingStates.LOADING;
		});
		builder.addCase(getInactiveTime.rejected, (draft: Users) => {
			draft.configData.loading = LoadingStates.IDLE;
		});

		// Get Store List
		builder.addCase(getStoreList.fulfilled, (draft: Users, { payload }) => {
			draft.storeList.stores = payload;
			draft.storeList.loading = LoadingStates.IDLE;
		});
		builder.addCase(getStoreList.pending, (draft: Users) => {
			draft.storeList.loading = LoadingStates.LOADING;
		});
		builder.addCase(getStoreList.rejected, (draft: Users, { payload }) => {
			draft.storeList.loading = LoadingStates.IDLE;
			setAuthTokenError(draft, payload);
		});
	}
});

// Actions
export const {
	updateUserToken,
	resetCreateGroup,
	resetToggleGroupActivation,
	resetEditGroup,
	resetCreateUser,
	resetActivateUser,
	resetResendTemporaryPassword,
	resetDeactivateUser,
	resetEditUser,
	updateUserSearchKeyPressed,
	updatedSelectedFilter,
	toggleFilter,
	clearFilter,
	updatedselectedRowsPerPage,
	updateCurrentGroup,
	resetSelectedGroupDetails,
	resetUserDetails,
	resetSelectedAdminDetails,
	resetAppData,
	updateAuthFlowStatus,
	resetAuthErr,
	updateSelectedOutletCode,
	updatedSelectedPageSize,
	resetEnquiryDetails,
	updateFilterInputs
} = userSlice.actions;

// Selector
export const isUserAuthDataAavailableSelector = (state: RootState): boolean => !!state?.users?.user;
export const getLoadingState = (state: RootState): LoadingStates => state?.users?.loading;
export const getLoadingStateForUserList = (state: RootState): LoadingStates => state?.users?.userGroupList.loading;
export const isLoggedInSelector = (state: RootState): boolean => !!state?.users?.userToken;
export const getLoggedInUserInfo = (state: RootState): Users["user"] => state?.users?.user;
export const getAccessToken = (state: RootState): string | undefined => state?.users?.userToken?.accessToken?.jwtToken;
export const getUserDetailList = (state: RootState): ApiUserResponse => state?.users?.userList.list;
export const getUserGroupList = (state: RootState): ApiUserResponse => state?.users?.userGroupList;
export const getUserPermissionListSelector = (state: RootState): Array<UserPermissionType> => state?.users?.userPermissionList;

// selector for capitastar number
export const getCardEnquiryDetailsSelector = (state: RootState): CardEnquiryResponse => state?.users?.cardEnquiryDetails?.details;
export const getMemberEnquiryDetailsSelector = (state: RootState): MemberEnquiryResponse => state?.users?.memberEnquiryDetails?.details;
export const loadingCardEnquiryDetailsSelector = (state: RootState) => state?.users?.cardEnquiryDetails?.loading;
export const loadingMemberEnquiryDetailsSelector = (state: RootState) => state?.users?.memberEnquiryDetails?.loading;
export const loadingDelinkMemberSelector = (state: RootState) => state?.users?.delinkMember?.loading;

// selector for permissions list
export const groupPermissionListSelector = (state: RootState) => state?.users?.groups.permissionsList;
export const loadingGroupPermissionSelector = (state: RootState) => state?.users?.groups.loading;

// check whether user has enter the search key
export const isUserSearchKeyPressed = (state: RootState): boolean => state?.users?.userSearchKeyPressed;

export const showHideFilter = (state: RootState): Filter => state?.users?.showFilter;

export const selectedFilterData = (state: RootState): ApiUserResponse => state?.users?.selectedFilters;
export const selectedRecordCount = (state: RootState): ApiUserResponse => state?.users?.selectedRowsPerPage;
export const selectedCurrentGroup = (state: RootState): string => state?.users?.currentGroup;

// selector for group list
export const groupsListSelector = (state: RootState) => state?.users?.groups.groupsList;
export const groupsListLoading = (state: RootState) => state?.users?.groups.loading;

// selector for selected group details
export const selectedGroupsDetailsSelector = (state: RootState) => state?.users?.groups.selectedGroupDetails;

// selector for selected admin user details
export const selectedUserDetailsSelector = (state: RootState) => state?.users?.adminUser?.selectedUserDetails;
export const usersDataLoadingSelector = (state: RootState) => state?.users?.adminUser?.loading;

// Selector for getting selected outlet code
export const selectedOutletCodeSelector = (state: RootState) => state?.users?.selectedOutletCode;
export const selectedPageSizeSelector = (state: RootState) => state?.users?.selectedPageSize;

export const hasUnaurizedErrorSelector = (state: RootState) => state?.users?.hasUnaurizedError;

// Get configurations and stores list
export const getConfigSelector = (state: RootState) => state?.users?.configData?.configuration;
export const getConfigDataLoading = (state: RootState) => state?.users?.configData?.loading;
export const getStoreListSelector = (state: RootState) => state?.users?.storeList?.stores;
export const getStoreListLoading = (state: RootState) => state?.users?.storeList?.loading;

// Get Admin Email
export const getAdminEmailSelector = (state: RootState) => state?.users?.configData?.adminEmail;

// Get Inactive Time
export const getInactiveTimeSelector = (state: RootState) => state?.users?.configData?.inactiveTime;

export const authenticationInProgress = (state: RootState) => state?.users?.waitforAuthcompletion;

// Outlet management
export const getOutletSelector = (state: RootState): { loading: LoadingStates; outlet: OutletItemV2 } => state?.users?.getOutlet;
export const getLastUpdatedOutletListSelector = (state: RootState): { loading: LoadingStates; outlets: OutletItemV2[] } => state?.users?.lastUpdatedOutletList;
export const createOutletSelector = (state: RootState): { loading: LoadingStates } => state?.users?.createOutlet;
export const getTagsSelector = (state: RootState): { loading: LoadingStates; tags: TagItem[] } => state?.users?.getTags;
export const getCategoryType3TagsSelector = (state: RootState): { loading: LoadingStates; tags: CategoryType3TagItem[] } => state?.users?.getCategoryType3Tags;

// Selector for filterInputs
export const filterInputsSelector = (state: RootState) => state.users.filterInputs;

// Reducers
export default userSlice.reducer;
